import type { UserRole } from '@/types/auth';
import type { AuthResponse } from './types';
import { createUser, updateUserProfile } from './userService';
import { supabase } from '@/lib/supabase';
import { createOrganization } from './organizationService';
import { getAppUrl, getMarketingUrl } from '@/utils/domains';

export const signUp = async (
  email: string,
  password: string,
  role: UserRole = "bizadmin",
  organizationName: string = '',
  firstName: string = '',
  lastName: string = '',
  priceId?: string
): Promise<AuthResponse> => {
  console.log('[AUTH API] Starting user signup process:', { email, role, firstName, lastName, organizationName, priceId });
  
  try {
    // 1. Create the user
    const userResponse = await createUser({ email, password, role });
    if (userResponse.status === 'error') {
      console.error('[AUTH API] Failed to create user:', userResponse.error);
      return userResponse;
    }
    const userId = userResponse.data.id;
    console.log('[AUTH API] User created successfully with ID:', userId);

    // IMPORTANT: Wait for session to be established before calling Edge Functions
    // The Edge Functions now require authentication, so we need a valid session
    let sessionEstablished = false;
    let retryCount = 0;
    const maxRetries = 5;
    
    while (!sessionEstablished && retryCount < maxRetries) {
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData.session?.user?.id === userId) {
        console.log('[AUTH API] Session established for new user');
        sessionEstablished = true;
      } else {
        console.log(`[AUTH API] Waiting for session... attempt ${retryCount + 1}`);
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
        retryCount++;
      }
    }
    
    if (!sessionEstablished) {
      console.warn('[AUTH API] Could not establish session for Edge Function calls, continuing anyway...');
    }

    // 2. Create the organization
    let organizationId: string | undefined;
    if ((role === 'superadmin' || role === 'bizadmin' || role === 'owner') && organizationName) {
      const orgResponse = await createOrganization(userId, organizationName);
      if (orgResponse.status === 'error') {
        console.error('[AUTH API] Failed to create organization:', orgResponse.error);
        return {
          data: null,
          error: orgResponse.error,
          status: 'error',
          message: `Failed to create organization: ${orgResponse.message || orgResponse.error.message}`
        };
      }
      organizationId = orgResponse.data.id;
      console.log('[AUTH API] Organization created with ID:', organizationId);
      
      // Manually verify that the organization exists
      try {
        const { data: orgCheck, error: orgCheckError } = await supabase
          .from('organizations')
          .select('id')
          .eq('id', organizationId)
          .single();
          
        if (orgCheckError || !orgCheck) {
          console.warn('[AUTH API] Organization not found after creation (may be a timing issue):', orgCheckError);
        } else {
          console.log('[AUTH API] Successfully verified organization exists in database');
          
          // Update user profile manually as a backup
          try {
            const { error: profileError } = await supabase
              .from('user_profiles')
              .upsert({
                id: userId,
                email,
                organization_id: organizationId,
                role,
                is_superuser: role === 'superadmin'
              });
              
            if (profileError) {
              console.error('[AUTH API] Failed to create user profile via upsert:', profileError);
            } else {
              console.log('[AUTH API] User profile created via upsert');
            }
          } catch (err) {
            console.error('[AUTH API] Error upserting profile:', err);
          }
          
          // Handle subscription if priceId is provided
          if (priceId) {
            try {
              const { data, error } = await supabase.functions.invoke('create-subscription', {
                body: {
                  organizationId,
                  priceId,
                  successUrl: `${getAppUrl()}/admin/subscription-success`,
                  cancelUrl: `${getAppUrl()}/admin/subscription-center`,
                },
              });
              if (error) {
                console.error('[AUTH API] Error creating Stripe subscription:', error);
              } else {
                console.log('[AUTH API] Stripe subscription created:', data);
              }
            } catch (err) {
              console.error('[AUTH API] Exception calling create-subscription:', err);
            }
          }
        }
      } catch (checkErr) {
        console.error('[AUTH API] Exception checking organization:', checkErr);
      }
    }

    // 3. Create the user profile securely via Edge Function
    if (userId && organizationId) {
      try {
        console.log('[AUTH API] Calling create-user-profile edge function with:', {
          userId,
          email,
          organizationId,
          role,
          isSuperuser: role === 'superadmin'
        });
        
        const { data: profileData, error: profileError } = await supabase.functions.invoke('create-user-profile', {
          body: {
            userId,
            email,
            organizationId,
            role,
            isSuperuser: role === 'superadmin'
          }
        });
        
        if (profileError) {
          console.error('[AUTH API] Failed to create user profile via Edge Function:', profileError);
          
          // Handle specific authentication/authorization errors
          if (profileError.message?.includes('Authentication required') || 
              profileError.message?.includes('authorization header')) {
            console.error('[AUTH API] Authentication error during profile creation - this may indicate a session issue');
            // Continue with signup process but log the issue
          } else if (profileError.message?.includes('Insufficient permissions')) {
            console.error('[AUTH API] Permission error during profile creation');
            // Continue with signup process but log the issue
          } else {
            console.error('[AUTH API] Unknown error during profile creation:', profileError.message);
          }
          
          // Don't return error here, continue with the process
          // The profile may have been created via the backup upsert above
        } else {
          console.log('[AUTH API] User profile created via Edge Function:', profileData);
        }
      } catch (profileErr) {
        console.error('[AUTH API] Exception creating user profile via Edge Function:', profileErr);
        // Continue with the process
      }
    }

    // 4. Update auth metadata to include organization ID directly
    if (userId && organizationId) {
      try {
        console.log('[AUTH API] Updating user metadata with organization ID:', organizationId);
        
        const { error: metadataError } = await supabase.auth.updateUser({
          data: { 
            organization_id: organizationId,
            role
          }
        });
        
        if (metadataError) {
          console.error('[AUTH API] Failed to update user metadata:', metadataError);
        } else {
          console.log('[AUTH API] User metadata updated successfully');
          
          // Force refresh session to get updated metadata
          try {
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) {
              console.error('[AUTH API] Error refreshing session after metadata update:', refreshError);
            } else {
              console.log('[AUTH API] Session refreshed with new metadata:', refreshData?.user?.user_metadata);
            }
          } catch (refreshErr) {
            console.error('[AUTH API] Exception refreshing session:', refreshErr);
          }
        }
      } catch (metadataErr) {
        console.error('[AUTH API] Exception updating user metadata:', metadataErr);
      }
    }

    // 5. Return the result
    // Send custom verification email using Resend via Edge Function
    if (userId && email) {
      try {
        console.log('[AUTH API] Getting verification URL from Supabase...');
        
        // Generate verification URL from Supabase
        const { data: verificationData, error: verificationError } = await supabase.auth.resend({
          type: 'signup',
          email,
          options: {
            emailRedirectTo: `${getMarketingUrl()}/auth?tab=signin`
          }
        });
        
        if (verificationError) {
          console.error('[AUTH API] Failed to generate verification token:', verificationError);
        } else {
          console.log('[AUTH API] Verification prepared:', !!verificationData);
          
          // Since Supabase already sent an email but we want to use our custom one,
          // we need to extract the verification URL (which we can't directly)
          // Instead, we'll construct our own URL but use Supabase's email verification
          
          // Call our custom email function
          try {
            console.log('[AUTH API] Sending custom verification email via Resend...');
            
            // Get the marketing domain for verification
            const appUrl = getMarketingUrl();
            
            await supabase.functions.invoke('send-verification-email', {
              body: {
                email,
                firstName,
                lastName,
                verificationUrl: `${appUrl}/auth?tab=signin&verification=pending`
              }
            });
            
            console.log('[AUTH API] Custom verification email sent successfully');
          } catch (emailErr) {
            console.error('[AUTH API] Error sending custom verification email:', emailErr);
          }
        }
      } catch (err) {
        console.error('[AUTH API] Error in verification email process:', err);
      }
    }

    // 6. Send admin notification email (async, don't block signup)
    if (userId && email) {
      try {
        console.log('[AUTH API] Sending admin notification email...');
        
        const tierType = priceId ? 'paid' : 'trial';
        
        // Call the notification function without waiting for completion
        supabase.functions.invoke('notify-new-signup', {
          body: {
            email,
            orgName: organizationName || 'Not specified',
            tier: tierType
          }
        }).then((response) => {
          if (response.error) {
            console.error('[AUTH API] Failed to send admin notification:', response.error);
          } else {
            console.log('[AUTH API] Admin notification sent successfully');
          }
        }).catch((error) => {
          console.error('[AUTH API] Error sending admin notification:', error);
        });
        
      } catch (notificationErr) {
        console.error('[AUTH API] Exception preparing admin notification:', notificationErr);
        // Don't fail the signup if notification fails
      }
    }
    
    console.log('[AUTH API] Signup process completed successfully');
    return {
      data: {
        user: userResponse.data,
        profile: {
          id: userId,
          role,
          first_name: firstName,
          last_name: lastName,
          organization_id: organizationId
        },
        organization: organizationId ? { id: organizationId, name: organizationName } : null
      },
      error: null,
      status: 'success',
      message: 'Account created successfully'
    };
  } catch (error) {
    console.error('[AUTH API] Unexpected error during signup process:', error);
    return {
      data: null,
      error: error instanceof Error ? error : new Error(String(error)),
      status: 'error',
      message: 'Unexpected error during account creation'
    };
  }
};

// Export the complete auth API
export const authApi = {
  signUp,
  createUser,
  updateUserProfile
};

export default authApi;
